import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useDays, useProjects, useSettings, useTasks } from '@/db/hooks'
import { useNow } from '@/hooks/useNow'
import { addTaskFromText } from '@/components/QuickAdd'
import { splitLines } from '@/kinds/task/parse'
import { clock, dayOf, joinTitles, markDay, morningLine, nightLength, nowIso } from '@/lib/day'
import { addDays, todayLocal } from '@/lib/time'
import { useScope } from '@/state/scope'
import { ALL } from '@/kinds/workspace/schema'
import { Button, Field, PageTitle } from '@/components/ui'
import { Phrases } from '@/components/Phrases'

/**
 * The morning brief at /morning, opened by the morning push: a line to get
 * you going, "I'm up", what is lined up (capped like Today), and a box to
 * empty your head into while you are still in bed. Each line becomes an
 * inbox task through the same parser as quick add.
 */
export const MorningView = () => {
  const tasks = useTasks() ?? []
  const projects = useProjects() ?? []
  const settings = useSettings()
  const days = useDays()
  const ws = useScope((s) => s.ws)
  const now = useNow(30_000)
  const today = todayLocal()
  const summary = dayOf(tasks, today)
  const line = morningLine(
    summary.left.map((t) => t.spec.title),
    settings.today_cap,
  )
  const rec = days.find((d) => d.spec.date === today)
  const night = days.find((d) => d.spec.date === addDays(today, -1))
  const slept = nightLength(night?.spec.slept_at, rec?.spec.woke_at)
  const [draft, setDraft] = useState('')
  const [added, setAdded] = useState<string[]>([])
  const shown = summary.left.slice(0, Math.max(1, settings.today_cap))
  const add = async () => {
    const lines = splitLines(draft)
    if (!lines.length) return
    setDraft('')
    const made: string[] = []
    for (const l of lines) {
      const r = await addTaskFromText(l, projects, { workspace: ws === ALL ? undefined : ws })
      if (r) made.push(String((r.spec as { title?: unknown }).title ?? l))
    }
    setAdded((a) => [...a, ...made])
  }
  return (
    <div data-testid="morning">
      <PageTitle
        eyebrow={new Date(now).toLocaleDateString(undefined, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        })}
      >
        {line.title}
      </PageTitle>
      <p className="text-lg leading-snug mb-4" data-testid="morning-line">
        {line.body}
      </p>
      <section className="card px-3.5 py-3 mb-5 flex items-center justify-between gap-3">
        {rec?.spec.woke_at ? (
          <div className="text-sm">
            <div>Up at {clock(rec.spec.woke_at)}</div>
            {/* last night as it was, named as last night: the horizon's 23:30 is tonight's nominal end */}
            {night?.spec.slept_at && (
              <Phrases
                as="div"
                className="text-xs text-muted"
                parts={[
                  `Lights out last night at ${clock(night.spec.slept_at)}`,
                  slept && `${slept} of sleep`,
                ]}
              />
            )}
          </div>
        ) : (
          <div className="text-sm text-muted">Say when you are up and it goes in the day.</div>
        )}
        {!rec?.spec.woke_at && (
          <Button
            variant="primary"
            className="shrink-0 whitespace-nowrap"
            onClick={() => void markDay(today, { woke_at: nowIso() })}
            data-testid="morning-wake"
          >
            I’m up
          </Button>
        )}
      </section>
      {shown.length > 0 && (
        <section className="mb-5">
          <div className="text-[13px] font-semibold text-muted mb-1.5">Lined up</div>
          <ul className="group-list" data-testid="morning-lined-up">
            {shown.map((t) => (
              <li key={t.id}>
                <Link
                  to="/tasks/$id"
                  params={{ id: t.id }}
                  className="block px-3.5 py-2.5 min-h-11 text-[15px]"
                >
                  {t.spec.title}
                </Link>
              </li>
            ))}
          </ul>
          {summary.left.length > shown.length && (
            <Link to="/" className="inline-block mt-2 text-xs text-muted hover:text-fg">
              The rest is on Today
            </Link>
          )}
        </section>
      )}
      <section className="mb-5">
        <Field label="Empty your head">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            placeholder="ring the vet&#10;book the car in >fri&#10;idea: garden bench"
            className="w-full rounded-2xl"
            data-testid="morning-dump"
          />
        </Field>
        {/* the button right under the box, with the hint beside it, so it never ends the page under the tab bar */}
        <div className="flex items-start justify-between gap-3 -mt-1">
          <span className="text-xs text-faint pt-1">
            {/* named, not counted */}
            {added.length
              ? `Added ${added.length <= 3 ? joinTitles(added) : 'those'} to the inbox.`
              : 'One per line, into the inbox; >today, +project and #labels work too.'}
          </span>
          <Button
            variant="primary"
            onClick={() => void add()}
            // keep the box focused through the tap: iOS re-lays the page out when the
            // keyboard goes, and the tap can land on something else
            onPointerDown={(e) => e.preventDefault()}
            disabled={!draft.trim()}
            data-testid="morning-add"
          >
            Add
          </Button>
        </div>
      </section>
      <p>
        <Link to="/" className="text-xs text-faint hover:text-fg">
          Today
        </Link>
      </p>
    </div>
  )
}
